/**
 * Sidebar — desktop persistent rail + mobile drawer. Orchestrates the nav
 * items registry, the alert-count hook, the desktop flyout, and the bottom
 * user section. Sub-pieces live in `./sidebar/`.
 *
 * The named exports (NAV_ITEMS, isItemOfflineAvailable, MobileMenuButton)
 * are re-exported from their new homes so existing call sites (BottomNav,
 * DashboardLayout, tests) keep working without import-path churn.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { usePassportAlerts } from "@/contexts/PassportAlertsContext";
import { useUnreadChat } from "@/contexts/UnreadChatContext";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import AltivLogo from "@/assets/altiv-logo.svg?react";
import useOnline from "@/hooks/useOnline";
import useETMSummary from "@/hooks/useETMSummary";
import { canReadETM } from "@/lib/permissions";
import { NAV_ITEMS, isItemOfflineAvailable, resolveNavItems } from "./sidebar/navItems";
import { useAlertCounts } from "./sidebar/useAlertCounts";
import { useNavFlyout } from "./sidebar/useNavFlyout";
import NavGroupItem from "./sidebar/NavGroupItem";
import NavLeafItem from "./sidebar/NavLeafItem";
import SidebarUserSection from "./sidebar/SidebarUserSection";
import MobileMenuButton from "./sidebar/MobileMenuButton";

export { NAV_ITEMS, isItemOfflineAvailable, MobileMenuButton };

export default function Sidebar({ mobileOpen, setMobileOpen, collapsed, setCollapsed }) {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const { hasExpiredPassports, hasExpiredStaffDocs } = usePassportAlerts();
  const { unreadTripIds } = useUnreadChat();
  const unreadChatCount = unreadTripIds.size;
  const navigate = useNavigate();
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState({});
  const { alertCount, systemAlertCount } = useAlertCounts(user);
  const { flyoutGroup, setFlyoutGroup, flyoutPos, flyoutRef, registerButton } = useNavFlyout();
  // ETM is restricted server-side; the helper mirrors the backend gate so
  // owner/accountant/ground_support don't fire a layout-level 403 here.
  const { actionCount: etmActionCount } = useETMSummary({ enabled: canReadETM(user) });
  const online = useOnline();
  // Memoize so the items array identity is stable across re-renders that
  // don't change role/permissions; otherwise NavGroupItem/NavLeafItem memo
  // barriers break on the `item` prop ref alone.
  const items = useMemo(
    () => resolveNavItems(user),
    [user?.role, user?.settings?.can_see_expenses, user?.settings?.can_see_fuel_analytics]
  );

  const toggleGroup = useCallback((key) => setOpenGroups((g) => ({ ...g, [key]: !g[key] })), []);

  // Auto-open any group whose child matches the current route on mobile
  // (inline accordion). Desktop uses a side flyout that stays closed by
  // default — auto-opening it on every page load would be noisy.
  useEffect(() => {
    if (!mobileOpen) return;
    const roleItems = NAV_ITEMS[user?.role] || [];
    const updates = {};
    roleItems.forEach((it) => {
      if (it.children && it.children.some((c) => c.path === location.pathname)) {
        updates[it.groupKey] = true;
      }
    });
    if (Object.keys(updates).length) {
      setOpenGroups((g) => ({ ...g, ...updates }));
    }
  }, [location.pathname, user?.role, mobileOpen]);

  // Wrap logout in useCallback so the prop ref passed to SidebarUserSection
  // is stable across re-renders. AuthContext's `logout` may be recreated on
  // any context change; the indirection here keeps the memo barrier intact.
  const handleLogout = useCallback(() => logout?.(), [logout]);

  // SidebarUserSection only needs full_name + role. Building a minimal
  // memoized projection keeps its React.memo barrier intact even when the
  // full user object gets re-fetched/reassigned by AuthContext.
  const userMini = useMemo(
    () => ({ full_name: user?.full_name, role: user?.role }),
    [user?.full_name, user?.role]
  );

  const handleNav = useCallback(
    (path) => {
      navigate(path);
      if (setMobileOpen) setMobileOpen(false);
    },
    [navigate, setMobileOpen]
  );

  // Per-group stable handlers keyed by groupKey so NavGroupItem's React.memo
  // barrier isn't broken by a fresh arrow on every parent render. Rebuilt
  // only when the visible items array identity changes (role/permission
  // swap) or when one of the underlying stable callbacks changes.
  const groupHandlers = useMemo(() => {
    const map = {};
    items.forEach((item) => {
      if (!item.children) return;
      const key = item.groupKey;
      map[key] = {
        onToggle: () => {
          if (mobileOpen) {
            toggleGroup(key);
          } else {
            setFlyoutGroup((cur) => (cur === key ? null : key));
          }
        },
        onChildNav: (child) => {
          setFlyoutGroup(null);
          handleNav(child.path);
        },
      };
    });
    return map;
    // `mobileOpen` is read inside onToggle so it must be a dep; rebuilding
    // group handlers when the drawer opens/closes is OK (it's a rare flip
    // and we want the closure to see the current value).
  }, [items, mobileOpen, toggleGroup, setFlyoutGroup, handleNav]);

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-[55] lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        data-testid="sidebar"
        data-sidebar="true"
        data-collapsed={collapsed}
        className={`fixed left-0 top-0 h-screen bg-[#0f172a] border-r border-slate-800 flex flex-col z-[60] transition-all duration-300
          ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          ${collapsed ? "lg:w-[68px] collapsed" : "lg:w-[240px]"}
          w-[260px]
        `}
      >
        {/* Logo */}
        <div
          className={`h-16 sm:h-20 flex items-center border-b border-slate-800 justify-between ${collapsed && !mobileOpen ? "px-0 justify-center" : "px-4"}`}
        >
          <div
            className={`flex items-center gap-2 ${collapsed && !mobileOpen ? "justify-center w-full" : ""}`}
          >
            <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center flex-shrink-0">
              <AltivLogo
                aria-label="Altiv"
                className={`w-16 h-16 sm:w-20 sm:h-20 ${theme === "light" ? "text-black" : "text-white"}`}
              />
            </div>
            {(!collapsed || mobileOpen) && (
              <span className="text-3xl font-medium text-white font-rubik">Altiv</span>
            )}
          </div>
          {/* Mobile close button */}
          <button
            onClick={() => setMobileOpen(false)}
            className="lg:hidden p-2 text-slate-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 min-h-0 overflow-y-auto py-4 px-2 space-y-1">
          {items.map((item) => {
            // Greyed out + lock when offline AND nothing inside is reachable
            // from the snapshot. For partial groups (some children offline-
            // available, some not), the group stays active and the children
            // are individually flagged.
            const itemLocked = !online && !isItemOfflineAvailable(item);

            if (item.children) {
              const inlineOpen = !!openGroups[item.groupKey] && mobileOpen;
              const flyoutOpen = flyoutGroup === item.groupKey && !mobileOpen;
              return (
                <NavGroupItem
                  key={item.groupKey}
                  item={item}
                  collapsed={collapsed}
                  mobileOpen={mobileOpen}
                  itemLocked={itemLocked}
                  inlineOpen={inlineOpen}
                  flyoutOpen={flyoutOpen}
                  flyoutPos={flyoutPos}
                  flyoutRef={flyoutRef}
                  buttonRef={registerButton(item.groupKey)}
                  hasExpiredPassports={hasExpiredPassports}
                  hasExpiredStaffDocs={hasExpiredStaffDocs}
                  online={online}
                  onToggle={groupHandlers[item.groupKey].onToggle}
                  onChildNav={groupHandlers[item.groupKey].onChildNav}
                />
              );
            }

            return (
              <NavLeafItem
                key={item.path}
                item={item}
                collapsed={collapsed}
                mobileOpen={mobileOpen}
                itemLocked={itemLocked}
                alertCount={alertCount}
                systemAlertCount={systemAlertCount}
                unreadChatCount={unreadChatCount}
                etmActionCount={etmActionCount}
                userRole={user?.role}
                hasExpiredPassports={hasExpiredPassports}
                hasExpiredStaffDocs={hasExpiredStaffDocs}
                onNav={handleNav}
              />
            );
          })}
        </nav>

        <SidebarUserSection
          user={userMini}
          collapsed={collapsed}
          mobileOpen={mobileOpen}
          onLogout={handleLogout}
        />

        {/* Collapse toggle - desktop only */}
        <button
          data-testid="sidebar-toggle"
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 bg-slate-800 border border-slate-700 rounded-full items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition-colors duration-200"
        >
          {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
        </button>
      </aside>
    </>
  );
}
